import { useCallback, useState } from 'react'
import classNames from 'classnames'
import { Button } from '@/atoms/Button'
import { ArrowRightIcon } from '@/atoms/Icons/ArrowRightIcon'
import { HeartIconFill } from '@/atoms/Icons/HeartIconFill'
import { LoadingSpinnerIcon } from '@/atoms/Icons/LoadingSpinnerIcon'
import { SceneIcon } from '@/atoms/Icons/SceneIcon'
import { Image } from '@/atoms/Image'
import { AngelLogo } from '@/atoms/Logos/AngelLogo'
import { ChosenLogo } from '@/atoms/Logos/ChosenLogo'
import { CollectibleLogo } from '@/atoms/Logos/CollectibleLogo'
import { FreelancersLogo } from '@/atoms/Logos/FreelancersLogo'
import { HisOnlySonLogo } from '@/atoms/Logos/HisOnlySonLogo'
import { OneColorLogoProps } from '@/atoms/Logos/LogoTypes'
import { TuttleTwinsLogo } from '@/atoms/Logos/TuttleTwinsLogo'
import { WingfeatherLogo } from '@/atoms/Logos/WingfeatherLogo'
import { CaptionLG, CaptionMD, CaptionSM, EyebrowSM, TitleXS } from '@/atoms/Text'
import { paths } from '@/constants/paths'
import { slugs } from '@/constants/slugs'
import { useMomentClaim } from '@/services/MomentsService'
import { MomentTypes, Nifty } from '@/types/codegen-federation'
import { getCloudinaryImageUrl } from '@/utils/Cloudinary'
import { useLocale } from '@/utils/LocaleUtil'
import { useSafeTrack } from '@/utils/analytics'
import { useTranslate } from '@/utils/translate/translate-client'

interface Props {
  collectibleType: 'frame' | 'scene'
  momentType?: MomentTypes
  collectible: Nifty | null
  shouldShowBusy: boolean
}

type collectibleEnabledSlugs = 'the-chosen' | 'his-only-son' | 'tuttle-twins' | 'freelancers' | 'wingfeather-saga'

const collectibleLogos: { [key in collectibleEnabledSlugs]?: React.FC<OneColorLogoProps> } = {
  'the-chosen': ChosenLogo,
  'his-only-son': HisOnlySonLogo,
  'tuttle-twins': TuttleTwinsLogo,
  freelancers: FreelancersLogo,
  'wingfeather-saga': WingfeatherLogo,
}

const DisplayLogo = (slug: string) => {
  if (slug in collectibleLogos) {
    const LogoComponent = collectibleLogos[slug as collectibleEnabledSlugs] || (() => <AngelLogo color="black" />)
    return <LogoComponent color="black" height={slug === slugs.hisOnlySon ? 32 : 70} />
  } else {
    return <AngelLogo color="black" />
  }
}

export const frameFormatter = (frame: string) => {
  const regex = /(\d{2}-\d{2}-\d{2}-\d{2})/
  const match = frame.match(regex)

  if (match && match[1]) {
    const timestamp = match[1].split('-')
    const cleanedTimestamp: string = `Frame ${timestamp[timestamp.length - 1]} at ${timestamp.slice(0, -1).join(':')}`
    return cleanedTimestamp
  }

  return frame
}

export const CollectibleUnboxingSceneDetails: React.FC<Props> = ({
  collectibleType,
  momentType,
  shouldShowBusy,
  collectible,
}) => {
  const { t } = useTranslate('account')
  const track = useSafeTrack()
  const [isMomentClaimed, setIsMomentClaimed] = useState(false)
  const { momentClaim } = useMomentClaim()
  const { locale } = useLocale()
  const thumbnailUrl = getCloudinaryImageUrl({ path: '/v1683403621/angel-studios/collectibles/unrevealed-thumb.webp' })
  const [thumbnailSrc, setThumbnailSrc] = useState(thumbnailUrl)

  const { id } = collectible || {}
  const frame = collectible?.frames?.[0]
  const frameUri = frame?.uri

  const reactionsFormatter = Intl.NumberFormat(locale, {
    notation: 'compact',
    maximumFractionDigits: 0,
  })

  const handleRevealButtonClick = useCallback(() => {
    if (collectible) {
      momentClaim(collectible.claimedStatus?.claimToken as string)
      track('Scene Claimed', {
        momentId: id,
        momentTitle: frame?.moment?.title,
        momentType: collectible.claimedStatus?.reasonEarned,
        path: 'account',
      })
      setIsMomentClaimed(true)
      setThumbnailSrc(frameUri || thumbnailUrl)
    }

    if (collectible && isMomentClaimed) {
      if (frame?.moment) {
        window.open(`${paths.share.moment}/${frame?.moment?.id}/frame/${frame?.id}`, '_blank')
      }
    }
  }, [collectible, frame?.id, frameUri, id, isMomentClaimed, frame?.moment, momentClaim, thumbnailUrl, track])

  const sceneTypeTitle = momentType === 'GOLDEN' ? t('golden', 'Golden') : t('collectible', 'Collectible')
  const sceneColor = momentType === 'GOLDEN' ? 'warm-03' : 'secondary-cool'

  const findSignature = (collectible: Nifty): string | null => {
    for (const signature of collectible?.signatures ?? []) {
      if (signature?.signatureUrl !== null) {
        return signature?.signatureUrl ?? null
      }
    }
    return null
  }

  if (!collectible) return null

  const hasSignature = findSignature(collectible) !== null

  const redeemCta =
    collectibleType === 'scene'
      ? t('redeemSceneV2', 'Reveal My Scene')
      : t('redeemFrameV2', 'Reveal My Collectible Frame')

  const totalOwners = frame?.moment?.totalFrames || 1

  return (
    <div className="mx-4 flex w-full shrink-0 snap-center flex-col items-center justify-center gap-1">
      <div className="my-3 flex flex-row flex-wrap items-center justify-evenly gap-3">
        {momentType && collectibleType === 'scene' && (
          <CollectibleLogo collectibleType={momentType} height={69} width={66} />
        )}
        {frame?.moment && <div>{DisplayLogo(frame?.moment.projectSlug || '')}</div>}
      </div>
      <div className="relative w-full overflow-hidden rounded-xl">
        <div className="aspect-h-9 aspect-w-16 relative">
          <Image src={thumbnailUrl} alt="Moment Frame" fill={true} className="z-0 cursor-pointer object-cover" />
          <Image
            src={thumbnailSrc}
            alt="Moment Frame"
            fill={true}
            className={classNames('z-0 cursor-pointer object-cover', {
              'blur-lg overflow-hidden': !isMomentClaimed,
              'animate-reveal': isMomentClaimed,
            })}
          />
          {hasSignature && isMomentClaimed && (
            <img src={findSignature(collectible) ?? ''} alt="Personalized signature" />
          )}
          {!isMomentClaimed && (
            <div className="absolute left-0 top-0 h-full w-full cursor-pointer" onClick={handleRevealButtonClick}>
              <div className="flex h-full items-center justify-center">
                <Button className="flex items-center space-x-1" variant="white">
                  <CaptionMD weight="bold" color="core-gray-950">
                    {t('reveal', 'Reveal')}
                  </CaptionMD>
                  {shouldShowBusy ? <LoadingSpinnerIcon color="black" /> : <ArrowRightIcon color="black" size={20} />}
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
      <div className="my-1 flex w-full flex-row justify-between">
        <div className="flex flex-row items-center gap-1">
          <SceneIcon color2={sceneColor} size={20} />
          <CaptionSM className={`text-${sceneColor}`}>{sceneTypeTitle} Scene</CaptionSM>
        </div>
        <div className="flex flex-row">
          <CaptionMD weight="bold">{reactionsFormatter.format(frame?.moment?.reactionCount || 0)}</CaptionMD>
          <HeartIconFill color="red-800" />
        </div>
      </div>
      <div className="w-full">
        <CaptionLG weight="bold">{frame?.moment?.title}</CaptionLG>
        {frame?.moment?.episode?.show?.type !== 'FILM' && (
          <CaptionMD>
            {t('episodeMetaInfo', 'Season {{seasonNumber}} Episode {{episodeNumber}}', {
              seasonNumber: frame?.moment?.episode?.season?.seasonNumber,
              episodeNumber: frame?.moment?.episode?.episodeNumber,
            })}
          </CaptionMD>
        )}
      </div>
      <div>
        <CaptionMD weight="bold" className="mt-4 text-center">
          {t('sceneInsights', 'Your Scene Insights')}:
        </CaptionMD>
        <div className="my-2 flex w-60 flex-col items-center rounded-lg bg-[#00000014] px-6 py-2">
          <EyebrowSM>{t('sceneOwners', 'SCENE OWNERS')}</EyebrowSM>
          <TitleXS>{t('oneOfManyOwners', '1 of {{totalOwners}} owners', { totalOwners })}</TitleXS>
        </div>
        <div className="my-2 flex w-60 flex-col items-center rounded-lg bg-[#00000014] px-6 py-2">
          <EyebrowSM>{t('yourFrame', 'Your Frame')}</EyebrowSM>
          <TitleXS>{frameFormatter(frame?.smpteTimecode || '')}</TitleXS>
        </div>
      </div>
      <Button
        className="flex items-center space-x-2.5 py-3"
        variant="black"
        disabled={shouldShowBusy}
        onClick={handleRevealButtonClick}
      >
        <CaptionLG weight="bold">{!isMomentClaimed ? redeemCta : 'Go To My Scene'}</CaptionLG>
        {shouldShowBusy ? <LoadingSpinnerIcon /> : <ArrowRightIcon size={24} />}
      </Button>
    </div>
  )
}
